
import { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, onSnapshot, collection, addDoc, serverTimestamp, query, orderBy, updateDoc, where, Timestamp, getDocs, runTransaction, arrayUnion, arrayRemove } from "firebase/firestore";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadString, getDownloadURL, deleteObject, uploadBytes } from "firebase/storage";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { USERS_COLLECTION, CHATS_COLLECTION, PROPOSALS_COLLECTION, NOTIFICATIONS_COLLECTION } from '../config/constants';

const FIREBASE_CONFIG = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID,
};

export const useFirebase = (showMessage) => {
    const [app] = useState(() => initializeApp(FIREBASE_CONFIG));
    const [auth] = useState(() => getAuth(app));
    const [db] = useState(() => getFirestore(app));
    const [storage] = useState(() => getStorage(app));
    const [functions] = useState(() => getFunctions(app, 'southamerica-east1'));
    const [messaging] = useState(() => {
        try {
            return getMessaging(app);
        } catch (e) {
            console.warn("FCM não suportado neste navegador.");
            return null;
        }
    });

    const [user, setUser] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);

    const requestNotificationPermission = useCallback(async () => {
        if (!messaging || !user || window.location.hostname === 'localhost') return;

        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                const token = await getToken(messaging, {
                    vapidKey: process.env.REACT_APP_VAPID_KEY
                });

                if (token) {
                    const userDocRef = doc(db, USERS_COLLECTION, user.uid);
                    await updateDoc(userDocRef, {
                        fcmTokens: arrayUnion(token)
                    });
                }
            }
        } catch (error) {
            console.error("Erro ao solicitar permissão de notificação:", error);
        }
    }, [messaging, user, db]);

    const signUp = useCallback(async (email, password, name, phone, role) => {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        await setDoc(doc(db, USERS_COLLECTION, user.uid), {
            roles: { [role]: true },
            [`profile_${role}`]: { name, email, phone, createdAt: serverTimestamp(), status: 'Disponível' }
        });
    }, [auth, db]);

    const addRoleToUser = useCallback(async (role, profileData) => {
        if (!user) throw new Error("Usuário não autenticado.");
        const userDocRef = doc(db, USERS_COLLECTION, user.uid);
        const userDoc = await getDoc(userDocRef);
        if (!userDoc.exists) throw new Error("Documento do usuário não encontrado.");
        const currentRoles = userDoc.data().roles || {};
        const newRoles = { ...currentRoles, [role]: true };
        const newProfilePayload = { ...profileData, createdAt: serverTimestamp(), status: role === 'servi' ? 'Disponível' : null };
        await updateDoc(userDocRef, { roles: newRoles, [`profile_${role}`]: newProfilePayload });
    }, [db, user]);

    const signIn = useCallback(async (email, password) => { return await signInWithEmailAndPassword(auth, email, password); }, [auth]);
    const logOut = useCallback(async () => { await signOut(auth); }, [auth]);
    const updateUserProfile = useCallback(async (uid, role, data) => { const userDocRef = doc(db, USERS_COLLECTION, uid); await updateDoc(userDocRef, { [`profile_${role}`]: data }); }, [db]);

    // --- FUNÇÕES DE UPLOAD (ADICIONADAS CIRURGICAMENTE) ---
    const uploadProfilePhoto = useCallback(async (uid, role, dataUrl) => {
        try {
            const storageRef = ref(storage, `profile_photos/${uid}_${role}.jpg`);
            await uploadString(storageRef, dataUrl, 'data_url');
            const url = await getDownloadURL(storageRef);
            await updateDoc(doc(db, USERS_COLLECTION, uid), { [`profile_${role}.photoUrl`]: url });
            showMessage("Foto de perfil atualizada!", "success");
        } catch (e) {
            showMessage("Erro ao salvar foto.", "error");
        }
    }, [db, storage, showMessage]);

    const uploadToGallery = useCallback(async (uid, role, dataUrl, file) => {
        try {
            const timestamp = Date.now();
            const storageRef = ref(storage, `galleries/${uid}/${timestamp}_${file.name}`);
            await uploadString(storageRef, dataUrl, 'data_url');
            const url = await getDownloadURL(storageRef);
            await updateDoc(doc(db, USERS_COLLECTION, uid), {
                [`profile_${role}.gallery`]: arrayUnion({ id: timestamp.toString(), url, createdAt: new Date().toISOString() })
            });
            showMessage("Adicionado ao portfólio!", "success");
        } catch (e) {
            showMessage("Erro ao subir imagem.", "error");
        }
    }, [db, storage, showMessage]);

    const uploadVideoToPortfolio = useCallback(async (uid, role, file) => {
        try {
            const timestamp = Date.now();
            const storageRef = ref(storage, `videos/${uid}/${timestamp}_${file.name}`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            await updateDoc(doc(db, USERS_COLLECTION, uid), {
                [`profile_${role}.videos`]: arrayUnion({ id: timestamp.toString(), url, createdAt: new Date().toISOString() })
            });
            showMessage("Vídeo salvo com sucesso!", "success");
        } catch (e) {
            showMessage("Erro ao carregar vídeo.", "error");
        }
    }, [db, storage, showMessage]);

    const removeFromGallery = useCallback(async (uid, role, item) => {
        try {
            await updateDoc(doc(db, USERS_COLLECTION, uid), { [`profile_${role}.gallery`]: arrayRemove(item) });
            showMessage("Item removido.", "info");
        } catch (e) {}
    }, [db, showMessage]);

    const removeVideoFromPortfolio = useCallback(async (uid, role, video) => {
        try {
            await updateDoc(doc(db, USERS_COLLECTION, uid), { [`profile_${role}.videos`]: arrayRemove(video) });
            showMessage("Vídeo removido.", "info");
        } catch (e) {}
    }, [db, showMessage]);

    const getAllServis = useCallback(async () => {
        const q = query(collection(db, USERS_COLLECTION), where("roles.servi", "==", true));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data().profile_servi }));
    }, [db]);

    const getOrCreateChat = useCallback(async (serviId, serviceTitle) => {
        try {
            const getOrCreateChatFunction = httpsCallable(functions, 'getOrCreateChat');
            const result = await getOrCreateChatFunction({ serviId, serviceTitle });
            return result.data.chatId;
        } catch (error) {
            console.error("Erro ao chamar a Cloud Function 'getOrCreateChat':", error);
            showMessage(error.message || "Não foi possível iniciar a conversa.", "error");
            return null;
        }
    }, [functions, showMessage]);

    const getChats = useCallback(async () => { try { const getChatsFunction = httpsCallable(functions, 'getChats'); const result = await getChatsFunction(); return result.data.chats.map(chat => ({ ...chat, lastMessageTimestamp: chat.lastMessageTimestamp ? new Date(chat.lastMessageTimestamp) : null, createdAt: chat.createdAt ? new Date(chat.createdAt) : null, })); } catch (error) { console.error("Erro ao buscar chats via Cloud Function:", error); showMessage(error.message || "Não foi possível carregar suas conversas.", "error"); return []; } }, [functions, showMessage]);
    const getProposals = useCallback(async (role) => { try { const getProposalsFunction = httpsCallable(functions, 'getProposals'); const result = await getProposalsFunction({ role }); return result.data.proposals.map(p => ({ ...p, createdAt: p.createdAt ? new Date(p.createdAt) : null, expiresAt: p.expiresAt ? new Date(p.expiresAt) : null, })); } catch (error) { console.error("Erro ao buscar propostas via Cloud Function:", error); return []; } }, [functions]);
    const getProposalsForChat = useCallback(async (chatId) => { try { const getProposalsFunction = httpsCallable(functions, 'getProposalsForChat'); const result = await getProposalsFunction({ chatId }); return result.data.proposals.map(p => ({ ...p, createdAt: p.createdAt ? new Date(p.createdAt) : null, expiresAt: p.expiresAt ? new Date(p.expiresAt) : null, })); } catch (error) { console.error("Erro ao buscar propostas para o chat:", error); showMessage(error.message || "Não foi possível carregar as propostas.", "error"); return []; } }, [functions, showMessage]);
    const sendMessage = useCallback(async (chatId, messageData) => { const messagesRef = collection(db, CHATS_COLLECTION, chatId, 'messages'); await addDoc(messagesRef, { ...messageData, timestamp: serverTimestamp() }); await updateDoc(doc(db, CHATS_COLLECTION, chatId), { lastMessage: messageData.text, lastMessageTimestamp: serverTimestamp() }); }, [db]);
    const sendQuoteMessage = useCallback(async (chatId, quoteData) => {
        try {
            const createQuoteFunction = httpsCallable(functions, 'createQuote');
            const result = await createQuoteFunction({ chatId, quoteData });
            if (result.data.success) { showMessage("Orçamento enviado com sucesso!", "success"); } else { throw new Error(result.data.message || "Falha ao criar o orçamento no backend."); }
        } catch (error) {
            console.error("Erro ao chamar a Cloud Function 'createQuote':", error);
            showMessage(error.message || "Ocorreu um erro ao enviar o orçamento.", "error");
        }
    }, [functions, showMessage]);
    const updateQuoteMessage = useCallback(async (proposalId, quoteData) => { console.warn("DEPRECATED: A função updateQuoteMessage foi desativada."); showMessage("A edição de orçamentos está temporariamente desativada.", "warning"); }, [showMessage]);
    const respondToQuote = useCallback(async (proposalId, response) => { console.log(`DEBUG: Chamando a Cloud Function 'respondToQuote' com proposalId: ${proposalId} e response: ${response}`); try { const respondToQuoteFunction = httpsCallable(functions, 'respondToQuote'); const result = await respondToQuoteFunction({ proposalId, response }); if (result.data.success) { showMessage("Resposta ao orçamento enviada!", "success"); } else { showMessage(result.data.diagnostic_message || "Falha no backend", "error"); } } catch (error) { console.error("DEBUG: Erro ao chamar a Cloud Function 'respondToQuote':", error); showMessage(error.message || "Ocorreu um erro ao processar sua resposta.", "error"); } }, [functions, showMessage]);

    const sendImageMessage = useCallback(async (chatId, file) => {
        try {
            if (!user) return;
            const timestamp = Date.now();
            const fileName = file.name || `img_${timestamp}.jpg`;
            const storagePath = `chat_images/${chatId}/${timestamp}_${fileName}`;
            const storageRef = ref(storage, storagePath);

            await uploadString(storageRef, file.dataUrl, 'data_url');
            const downloadURL = await getDownloadURL(storageRef);

            const messagesRef = collection(db, CHATS_COLLECTION, chatId, 'messages');
            await addDoc(messagesRef, {
                senderId: user.uid,
                type: 'image',
                imageUrl: downloadURL,
                text: '📷 Imagem',
                timestamp: serverTimestamp()
            });
            await updateDoc(doc(db, CHATS_COLLECTION, chatId), {
                lastMessage: '📷 Imagem',
                lastMessageTimestamp: serverTimestamp()
            });
        } catch (error) {
            console.error("Erro ao enviar imagem:", error);
            showMessage("Falha ao enviar a imagem. Verifique sua conexão.", "error");
        }
    }, [db, storage, user, showMessage]);

    const sendInvoiceMessage = useCallback(async (chatId, invoiceData) => {
        try {
            await runTransaction(db, async (transaction) => {
                const chatRef = doc(db, CHATS_COLLECTION, chatId);
                const messagesRef = collection(db, CHATS_COLLECTION, chatId, 'messages');

                const chatDoc = await transaction.get(chatRef);
                if (!chatDoc.exists) { throw "Chat não encontrado!"; }

                const lastMessageText = `Cobrança final enviada. Total: R$${invoiceData.total.toFixed(2)}.`;

                const newMessageRef = doc(messagesRef);
                transaction.set(newMessageRef, {
                    senderId: invoiceData.serviId,
                    text: lastMessageText,
                    type: 'invoice',
                    invoice: { ...invoiceData, status: 'Aguardando Pagamento' },
                    timestamp: serverTimestamp(),
                });

                transaction.update(chatRef, {
                    lastMessage: lastMessageText,
                    lastMessageTimestamp: serverTimestamp(),
                    status: 'Aguardando Pagamento'
                });
            });
            showMessage("Cobrança final enviada com sucesso!", "success");
        } catch (error) {
            console.error("Erro ao enviar cobrança:", error);
            showMessage("Ocorreu um erro ao enviar a cobrança.", "error");
        }
    }, [db, showMessage]);

    const processPayment = useCallback(async (chatId, messageId) => {
        try {
            const confirmPaymentFunction = httpsCallable(functions, 'confirmPayment');
            const result = await confirmPaymentFunction({ chatId, messageId });

            if (result.data.success) {
                showMessage("Pagamento confirmado com sucesso!", "success");
            } else {
                throw new Error("Falha ao confirmar o pagamento no servidor.");
            }
        } catch (error) {
            console.error("Erro ao processar pagamento via Cloud Function:", error);
            showMessage(error.message || "Ocorreu um erro ao processar o pagamento.", "error");
        }
    }, [functions, showMessage]);

    const submitRating = useCallback(async (targetUserId, targetRole, ratingData) => {
        try {
            const submitRatingFunction = httpsCallable(functions, 'submitRating');
            const result = await submitRatingFunction({
                targetUserId,
                targetRole,
                rating: ratingData.rating,
                comment: ratingData.comment,
                relatedChatId: ratingData.relatedChatId
            });

            if (result.data.success) {
                showMessage("Obrigado! Sua avaliação foi enviada com sucesso.", "success");
            } else {
                throw new Error("Falha ao enviar avaliação no servidor.");
            }
        } catch (error) {
            console.error("Erro ao submeter avaliação: ", error);
            showMessage("Não foi possível enviar sua avaliação.", "error");
        }
    }, [functions, showMessage]);

    const markNotificationAsDone = useCallback(async (notificationId) => {
        try {
            const notificationRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
            await updateDoc(notificationRef, { status: 'done' });
        } catch (error) {
            console.error("Erro ao marcar notificação como lida:", error);
        }
    }, [db]);

    const cancelService = useCallback(async (chatId, reason = 'Atendimento encerrado sem acordo.') => {
        const chatDocRef = doc(db, CHATS_COLLECTION, chatId);
        try {
            await updateDoc(chatDocRef, {
                status: 'Cancelado',
                lastMessage: reason,
                lastMessageTimestamp: serverTimestamp()
            });
            showMessage("Atendimento encerrado.", "info");
        } catch (error) {
            console.error("Erro ao encerrar atendimento:", error);
            showMessage("Não foi possível encerrar o atendimento.", "error");
        }
    }, [db, showMessage]);

    const getRealtimeMessages = useCallback((chatId, callback) => {
        const messagesRef = collection(db, CHATS_COLLECTION, chatId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        return onSnapshot(q, (snapshot) => {
            callback(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
    }, [db]);
    const getRealtimeChats = useCallback((uid, callback) => {
        if (!uid) return () => {};
        const q = query(collection(db, CHATS_COLLECTION), where("participants", "array-contains", uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const chats = snapshot.docs.map(doc => {
                const data = doc.data();
                const lastMessageTimestamp = data.lastMessageTimestamp?.toDate ? data.lastMessageTimestamp.toDate() : new Date();
                const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date();
                return { id: doc.id, ...data, lastMessageTimestamp, createdAt, };
            });
            callback(chats);
        }, (error) => { console.error("Erro ao buscar chats em tempo real:", error); callback([]); });
        return unsubscribe;
    }, [db]);

    const getPendingFutureCommitments = useCallback(async (uid) => {
        if (!uid) return [];
        try {
            const q = query(
                collection(db, PROPOSALS_COLLECTION),
                where("clientId", "==", uid),
                where("status", "==", "accepted")
            );
            const snap = await getDocs(q);
            const proposals = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            if (proposals.length === 0) return [];

            const results = await Promise.all(proposals.map(async (p) => {
                const chatDoc = await getDoc(doc(db, CHATS_COLLECTION, p.chatId));
                if (chatDoc.exists()) {
                    const status = chatDoc.data().status;
                    if (['Finalizado', 'Cancelado', 'Pago', 'Concluído'].includes(status)) {
                        return p;
                    }
                }
                return null;
            }));

            return results.filter(r => r !== null);
        } catch (e) {
            console.error("Erro ao buscar compromissos futuros:", e);
            return [];
        }
    }, [db]);

    const updateChatStatus = useCallback(async (chatId, newStatus) => { try { const chatDocRef = doc(db, CHATS_COLLECTION, chatId); await updateDoc(chatDocRef, { status: newStatus }); showMessage(`Status do serviço atualizado para: ${newStatus}`, 'success'); } catch (error) { console.error("Erro ao atualizar status do chat:", error); showMessage("Falha ao atualizar o status do serviço.", "error"); } }, [db, showMessage]);
    const scheduleService = useCallback(async (chatId, scheduledDate) => { try { const chatDocRef = doc(db, CHATS_COLLECTION, chatId); await updateDoc(chatDocRef, { status: 'Agendado', scheduledAt: scheduledDate, lastMessage: `Serviço agendado para ${scheduledDate.toLocaleDateString('pt-BR')} às ${scheduledDate.toLocaleTimeString('pt-BR', {hour: '2-digit', minute:'2-digit'})}`, lastMessageTimestamp: serverTimestamp(), }); showMessage("Serviço agendado com sucesso!", "success"); } catch (error) { console.error("Erro ao agendar serviço:", error); showMessage("Falha ao agendar o serviço.", "error"); } }, [db, showMessage]);
    const finalizeChat = useCallback(async (chatId, finalValue) => {
        try {
            const chatDocRef = doc(db, CHATS_COLLECTION, chatId);
            await updateDoc(chatDocRef, {
                status: 'Aguardando Pagamento',
                finalValue: finalValue,
                finishedAt: serverTimestamp(),
                lastMessage: `Serviço finalizado com o valor de R$${finalValue.toFixed(2)}. Aguardando confirmação de pagamento.`,
                lastMessageTimestamp: serverTimestamp()
            });
            showMessage("Serviço finalizado! Aguardando pagamento do cliente.", "success");
        } catch (error) {
            showMessage("Erro ao finalizar o serviço.", "error");
            console.error("Erro ao finalizar chat: ", error);
        }
    }, [db, showMessage]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUser(user);
            setIsAuthReady(true);
        });
        return unsubscribe;
    }, [auth]);

    useEffect(() => {
        if (!messaging) return;
        const unsubscribe = onMessage(messaging, (payload) => {
            showMessage(`${payload.notification.title}: ${payload.notification.body}`, "info");
        });
        return unsubscribe;
    }, [messaging, showMessage]);

    return {
        user, auth, db, storage, isAuthReady, functions,
        signUp, signIn, logOut, updateUserProfile, addRoleToUser, getAllServis,
        getOrCreateChat,
        getChats,
        getProposals,
        getProposalsForChat,
        sendMessage,
        sendQuoteMessage,
        updateQuoteMessage,
        sendImageMessage,
        respondToQuote,
        sendInvoiceMessage,
        processPayment,
        submitRating,
        markNotificationAsDone,
        cancelService,
        getRealtimeMessages,
        getRealtimeChats,
        getPendingFutureCommitments,
        updateChatStatus,
        scheduleService,
        finalizeChat,
        requestNotificationPermission,
        uploadProfilePhoto, uploadToGallery, uploadVideoToPortfolio, removeFromGallery, removeVideoFromPortfolio
    };
};
